import { AfterViewInit, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { expandCollapse } from '../_shared/animations/app-animations';
import { FormBuilder } from '@angular/forms';
import {
  AccountService,
  AlertService,
  CampaignProductService,
  CampaignService,
  CampaignTrackingService,
  CRMCampaignService,
  FunnelStepService,
  GuiService,
  MerchantService,
  ReportsConfigService,
  RetentionReportService,
  SideNavService,
  StorageService,
  UserService,
  CancelReasonCategoryService,
  FunnelService,
} from '../_services';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ReportsAbstractV3Component } from './reports-abstract-v3.component';
import { MultiOptions } from '../custom-multi-select/multi-select';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { subscriptionRetentionFields, subscriptionRetentionFilters } from './utils/constants';
import { reportsFieldDataV3 } from './utils/reportsFieldData';
import { cloneDeep } from 'lodash';
import { ReportDimensions, ReportTypes, ReportVersion } from './models/reports';
import { Observable } from 'rxjs';

@Component({
  moduleId: module.id.toString(),
  selector: 'subscription-retention-report-v3',
  templateUrl: './templates/reports-v3.component.html',
  styleUrls: ['./styles/reports-v2.component.scss', './styles/reports-v3.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class SubscriptionRetentionReportV3Component
  extends ReportsAbstractV3Component
  implements OnInit, AfterViewInit
{
  fields: MultiOptions = cloneDeep(subscriptionRetentionFields);
  filters: MultiOptions = cloneDeep(subscriptionRetentionFilters);
  reportType = ReportTypes.SUBSCRIPTION_RETENTION;
  pctFieldMap: { [key: string]: string } = {
    subscription_cancel: 'subscription_cancel_pct',
    subscription_save: 'subscription_save_pct',
  };

  overviewFieldMap: { [summaryField: string]: string } = {
    cancel_subscription_request: 'cancel_requests',
    subscription_cancel: 'cancelled',
    cancel_subscription_request_abandoned: 'abandoned',
    subscription_save: 'saved',
    save_subscription_rate: 'retention_rate',
  };

  predefinedFields = ['exclude_zeroes', 'exclude_tests'];
  commonReportsFields = [
    'cancel_subscription_request',
    'subscription_cancel',
    'subscription_save',
    'cancel_subscription_request_abandoned',
    'save_subscription_rate',
  ];

  summaryFields = [
    'cancel_subscription_request',
    'subscription_cancel',
    'cancel_subscription_request_abandoned',
    'subscription_save',
    'save_subscription_rate',
  ];

  selectedDimensions = [
    {
      model: 'cancel_reason',
      label: 'Cancel Reason',
      selected: true,
      disabled: false,
    },
  ];

  fieldsToExcludeFromReport: string[] = ['step_name', 'step_slug'];
  fieldsNotHavingEmptyValue = [
    ReportDimensions.CancelReason,
    ReportDimensions.LastSubscriptionStep,
    ReportDimensions.LastSubscriptionInput,
  ];
  switchFilterKeyMapping = [
    { from: ReportDimensions.ProductPath, to: ReportDimensions.SubscriptionPath },
    { from: ReportDimensions.InputName, to: ReportDimensions.LastSubscriptionInput },
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    public reportsService: RetentionReportService,
    protected modalService: NgxSmartModalService,
    protected campaignService: CampaignService,
    protected reportsConfigService: ReportsConfigService,
    protected trackingSourceService: CampaignTrackingService,
    protected userService: UserService,
    protected accountService: AccountService,
    protected campaignProductService: CampaignProductService,
    protected crmCampaignService: CRMCampaignService,
    protected storageService: StorageService,
    protected stepService: FunnelStepService,
    protected merchantService: MerchantService,
    protected cancelReasonCategoryService: CancelReasonCategoryService,
    protected funnelService: FunnelService,
    protected guiService: GuiService,
    protected sideNavService: SideNavService
  ) {
    super(
      router,
      location,
      route,
      alertService,
      reportsService,
      campaignService,
      reportsConfigService,
      trackingSourceService,
      userService,
      accountService,
      campaignProductService,
      crmCampaignService,
      storageService,
      stepService,
      merchantService,
      modalService,
      cancelReasonCategoryService,
      funnelService,
      guiService
    );
    this.fieldData = reportsFieldDataV3;
    this.showExcludePrefulfillmentCancelsFilter = true;
    this.showExcludeAbandonsFilter = true;
    this.bannerTitle = 'Subscription Retention Report';
    this.bannerSubtitle =
      'This report shows subscription-level retention outcomes, including saves, cancellations, abandonment, and offer performance for active subscriptions. Use dimensions and filters to dive deeper and understand what’s happening, where it’s happening, and why.';
  }

  ngOnInit() {
    super.ngOnInit();
    this.sideNavService.setSideNavExpanded(false);
  }

  ngAfterViewInit() {
    if (this.panel) {
      this.panel.expanded = true;
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.selectedDimensions, event.previousIndex, event.currentIndex);
  }

  getQueryForSummary() {
    const query = super.getQueryForSummary();
    query['report_type'] = this.reportType;
    query['report_version'] = ReportVersion.Clickhouse;
    this.switchFilterKeys(query);
    this.applyDashboardNavFilters(query);
    return query;
  }

  getQueryForReport() {
    const query = super.getQueryForReport();
    query['report_type'] = this.reportType;
    query['report_version'] = ReportVersion.Clickhouse;
    query['show_percentage'] = this.showPercentage;
    this.switchFilterKeys(query);
    this.applyDashboardNavFilters(query);
    this.expandMergedDimensionFields(query);
    return query;
  }

  getSummaryApiCalls(): Observable<any> {
    return this.reportsService.getSummary(this.getQueryForSummary());
  }
}
